/**
 * Orbit Collection — Ransom Supply
 *
 * Vanilla custom element. No libraries.
 * Custom elements auto-upgrade whenever Shopify re-renders a section, so this
 * needs no shopify:section:load wiring and can never double-bind listeners.
 * All motion is transform/opacity inside a single rAF loop that idles when
 * nothing is moving.
 */

const TWO_PI = Math.PI * 2;
const FRONT = Math.PI / 2; // sin(a) === 1 -> nearest the viewer
const DRAG_THRESHOLD = 6; // px of travel before a pointerup stops counting as a click
const FIT_MARGIN = 16; // breathing room above and below the ring when fitting height

// Must match the .orbit__center top values in orbit-collection.css. The ring is
// pushed away from whichever edge the type occupies, so these two numbers are
// the single source of truth for the mobile composition.
const MOBILE_TEXT_TOP = { above: 0.19, below: 0.81 };

class OrbitCollection extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.stage = this.querySelector('[data-orbit-stage]');
    this.centre = this.querySelector('[data-orbit-center]');
    this.items = Array.from(this.querySelectorAll('[data-orbit-item]'));
    this.panels = Array.from(this.querySelectorAll('[data-orbit-panel]'));
    this.count = this.items.length;

    if (!this.stage || this.count === 0) return;
    this.initialised = true;

    // ---- settings from Liquid -------------------------------------------
    this.autoRotate = this.dataset.autorotate === 'true';
    this.speed = (parseFloat(this.dataset.speed) || 3) * 0.00006; // rad/ms
    this.tilt = (parseFloat(this.dataset.tilt) || 52) / 100;
    this.radiusPct = (parseFloat(this.dataset.radius) || 40) / 100;
    this.radiusPctMobile = (parseFloat(this.dataset.radiusMobile) || 88) / 100;
    this.itemPct = (parseFloat(this.dataset.itemSize) || 17) / 100;
    this.ratio = parseFloat(this.dataset.ratio) || 5 / 7; // width / height
    // 'above' | 'below' | 'hidden' — where the active product's type sits on
    // phones. Anything unrecognised falls back to the original composition.
    this.mobileText = this.dataset.mobileText === 'below' || this.dataset.mobileText === 'hidden'
      ? this.dataset.mobileText
      : 'above';
    // 'inside' | 'hidden' — on desktop the type has always lived inside the
    // ring; hiding it lets the ring keep its natural tilt and run larger.
    this.desktopText = this.dataset.desktopText === 'hidden' ? 'hidden' : 'inside';
    // On phones a fixed stage height leaves the flat ring floating in a tall
    // empty box. Fitting collapses the stage to what the composition needs and
    // treats the height setting as a maximum.
    this.fitHeight = this.dataset.fitHeightMobile !== 'false';

    // ---- state -----------------------------------------------------------
    this.step = TWO_PI / this.count;
    this.phi = FRONT; // item 0 starts at the front
    this.velocity = 0;
    this.target = this.phi;
    this.snapping = false;
    this.dragging = false;
    this.hovering = false;
    this.inView = true;
    this.activeIndex = -1;
    this.rafId = null;
    this.lastTime = 0;
    this.moved = 0;
    this.pointerId = null;
    this.appliedH = 0; // last stage height this component wrote, to stop RO loops

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    // ---- bind ------------------------------------------------------------
    this.tick = this.tick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);

    // ---- listeners -------------------------------------------------------
    this.stage.addEventListener('pointerdown', this.onPointerDown);
    this.stage.addEventListener('pointermove', this.onPointerMove);
    this.stage.addEventListener('pointerup', this.onPointerUp);
    this.stage.addEventListener('pointercancel', this.onPointerUp);
    this.stage.addEventListener('click', this.onClickCapture, true);
    this.stage.addEventListener('wheel', this.onWheel, { passive: false });
    this.stage.addEventListener('dragstart', preventDefault);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.stage);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          if (this.inView) this.start();
        },
        { rootMargin: '120px' }
      );
      this.intersectionObserver.observe(this);
    }

    if (this.reduced.addEventListener) {
      this.reduced.addEventListener('change', this.onMotionChange);
    }

    // ---- go --------------------------------------------------------------
    this.measure();
    this.render();
    // Fonts can land after first paint and change the centre block's height.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.initialised) this.onResize();
      });
    }
    if (this.shouldAutoRotate()) this.start();
  }

  disconnectedCallback() {
    if (!this.initialised) return;
    this.stop();

    this.stage.removeEventListener('pointerdown', this.onPointerDown);
    this.stage.removeEventListener('pointermove', this.onPointerMove);
    this.stage.removeEventListener('pointerup', this.onPointerUp);
    this.stage.removeEventListener('pointercancel', this.onPointerUp);
    this.stage.removeEventListener('click', this.onClickCapture, true);
    this.stage.removeEventListener('wheel', this.onWheel);
    this.stage.removeEventListener('dragstart', preventDefault);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointerleave', this.onLeave);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('resize', this.onResize);

    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    if (this.reduced.removeEventListener) {
      this.reduced.removeEventListener('change', this.onMotionChange);
    }

    this.stage.style.height = '';
    this.appliedH = 0;
    clearTimeout(this.wheelTimer);
    this.initialised = false;
  }

  /* Layout — recomputed at runtime so any product count, image size and
     viewport width produce a composed ring: products never overlap each other,
     the ring never outgrows the section, and nothing sits under the type. */

  measure() {
    const rect = this.stage.getBoundingClientRect();
    if (!rect.width) return;

    // Read before writing, so we don't force an extra reflow.
    const centreH = this.centre ? this.centre.getBoundingClientRect().height : 0;

    this.w = rect.width;
    this.h = rect.height;
    this.mobile = this.w < 640;

    // With the type hidden there is no band to reserve, so the ring gets more
    // of the section and the products can run larger.
    const mobileHidden = this.mobile && this.mobileText === 'hidden';
    const desktopHidden = !this.mobile && this.desktopText === 'hidden';

    // Desktop never fits: its height setting is the composition.
    if (!this.mobile && this.appliedH) {
      this.stage.style.height = '';
      this.appliedH = 0;
      this.h = this.stage.getBoundingClientRect().height;
    }

    const pct = this.mobile ? this.radiusPctMobile : this.radiusPct;
    this.rx = this.w * pct;

    // --- item size --------------------------------------------------------
    // Widest an item can be before neighbours touch at the front of the ring.
    const arcLimit = 2 * this.rx * Math.sin(Math.PI / Math.max(this.count, 2)) * 0.9;
    // A narrow viewport needs proportionally larger product images to read.
    const requested = this.w * this.itemPct * (this.mobile ? 1.9 : 1);
    // Hard cap tied to section height, so a wide viewport can never grow the
    // products past what the section (plus the centre type) can hold. When the
    // stage is fitting to content the configured height is a budget, not a
    // constraint the products have to shrink inside.
    const budgetH = this.mobile && this.fitHeight ? Math.max(this.h, this.appliedH || this.h) : this.h;
    let capPct;
    if (this.mobile) capPct = mobileHidden ? 0.52 : 0.4;
    else capPct = desktopHidden ? 0.46 : 0.38;
    const heightCap = budgetH * capPct * this.ratio;

    let iw = Math.min(requested, arcLimit, heightCap);
    iw = Math.max(iw, 56);

    this.itemW = iw;
    this.itemH = iw / this.ratio;
    this.cx = this.w / 2;

    // --- ring depth -------------------------------------------------------
    // Back items render at 0.58 scale and front at 1.0, so the ring extends
    // 0.3*itemH above its top point and 0.5*itemH below its bottom point.
    // ry is clamped so that whole extent fits inside the section.
    let band = budgetH;
    if (this.mobile) band = mobileHidden ? budgetH * 0.92 : budgetH * 0.62;
    const maxRy = Math.max((band - 0.85 * this.itemH) / 2, 40);
    // Mobile runs a ring wider than the viewport: only the front arc shows, so
    // the active product reads large and its neighbours bleed off the edges.
    this.ry = Math.min(this.rx * (this.mobile ? this.tilt * 0.5 : this.tilt), maxRy);

    // --- collapse the stage to what the composition actually needs ---------
    // The mobile ring is deliberately flat, so a tall fixed stage leaves it
    // floating in empty space. Fitting removes that dead band entirely.
    if (this.mobile && this.fitHeight) {
      const extent = 2 * this.ry + 0.8 * this.itemH;
      const needed = mobileHidden
        ? extent + 2 * FIT_MARGIN
        : extent + centreH + 3 * FIT_MARGIN;
      const fitted = Math.round(Math.max(Math.min(needed, budgetH), 180));
      if (Math.abs(fitted - this.appliedH) > 1) {
        this.appliedH = fitted;
        this.stage.style.height = fitted + 'px';
      }
      this.h = fitted;
    }

    // --- keep the centre type clear ---------------------------------------
    if (this.mobile) {
      // Highest and lowest the ring's centre can sit without clipping itself.
      const topLimit = this.ry + 0.3 * this.itemH;
      const bottomLimit = this.h - this.ry - 0.5 * this.itemH;

      if (mobileHidden) {
        // Nothing to clear: centre the ring's visible extent, as on desktop.
        this.cy = this.h * 0.5 - 0.1 * this.itemH;
      } else if (this.mobileText === 'below') {
        // Pull the ring UP until its lowest item clears the type beneath it.
        const centreTop = this.h * MOBILE_TEXT_TOP.below - centreH / 2 - FIT_MARGIN;
        const maxCy = centreTop - this.ry - 0.5 * this.itemH;
        this.cy = Math.max(Math.min(this.h * 0.34, maxCy), Math.min(topLimit, maxCy));
      } else {
        // Push the ring DOWN until its highest item clears the type above it.
        const centreBottom = this.h * MOBILE_TEXT_TOP.above + centreH / 2 + FIT_MARGIN;
        const minCy = centreBottom + this.ry + 0.3 * this.itemH;
        this.cy = Math.min(Math.max(this.h * 0.66, minCy), Math.max(bottomLimit, minCy));
      }
    } else {
      // With the type showing, widen the ring until there is a clear band for
      // it inside. With it hidden, leave the natural tilt alone — forcing a
      // band open for text that isn't there just flattens the composition.
      if (!desktopHidden) {
        const minRy = (centreH + 40 + 0.8 * this.itemH) / 2;
        this.ry = Math.min(Math.max(this.ry, minRy), maxRy);
      }
      // Centre the ring's visible extent, not its geometric centre: the front
      // item hangs 0.5*itemH low while the back item rises only 0.3*itemH.
      this.cy = this.h * 0.5 - 0.1 * this.itemH;
    }

    this.style.setProperty('--orbit-item-w', Math.round(iw) + 'px');
    this.style.setProperty('--orbit-item-h', Math.round(this.itemH) + 'px');
  }

  render() {
    if (!this.rx) return;

    let bestIndex = 0;
    let bestDepth = -2;

    for (let i = 0; i < this.count; i += 1) {
      const angle = this.phi + i * this.step;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);
      const depth = (sin + 1) / 2; // 0 = far, 1 = front

      const x = this.cx + cos * this.rx - this.itemW / 2;
      const y = this.cy + sin * this.ry - this.itemH / 2;
      const scale = 0.58 + 0.42 * depth;
      const opacity = 0.16 + 0.84 * Math.pow(depth, 1.6);

      const el = this.items[i];
      el.style.transform =
        'translate3d(' + x.toFixed(2) + 'px, ' + y.toFixed(2) + 'px, 0) scale(' + scale.toFixed(4) + ')';
      el.style.opacity = opacity.toFixed(3);
      el.style.zIndex = String(Math.round(depth * 100));

      if (sin > bestDepth) {
        bestDepth = sin;
        bestIndex = i;
      }
    }

    if (bestIndex !== this.activeIndex) this.setActive(bestIndex);
  }

  setActive(index) {
    this.activeIndex = index;

    for (let i = 0; i < this.items.length; i += 1) {
      this.items[i].classList.toggle('is-active', i === index);
    }

    for (let i = 0; i < this.panels.length; i += 1) {
      const on = i === index;
      const panel = this.panels[i];
      panel.classList.toggle('is-active', on);
      panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      panel.querySelectorAll('a').forEach((a) => {
        a.tabIndex = on ? 0 : -1;
      });
    }
  }

  /* Loop ---------------------------------------------------------------- */

  start() {
    if (this.rafId === null) {
      this.lastTime = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }
  }

  stop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  shouldAutoRotate() {
    return (
      this.autoRotate &&
      !this.reduced.matches &&
      !this.hovering &&
      !this.dragging &&
      !document.hidden &&
      this.inView
    );
  }

  tick(now) {
    const dt = this.lastTime ? Math.min(now - this.lastTime, 50) : 16;
    this.lastTime = now;

    let moving = false;

    if (this.dragging) {
      moving = true;
    } else if (this.snapping) {
      const delta = this.target - this.phi;
      if (Math.abs(delta) < 0.0005 || this.reduced.matches) {
        this.phi = this.target;
        this.snapping = false;
      } else {
        this.phi += delta * 0.14;
        moving = true;
      }
    } else if (Math.abs(this.velocity) > 0.00005) {
      this.phi += this.velocity * dt;
      this.velocity *= Math.pow(0.9955, dt);
      moving = true;
      if (Math.abs(this.velocity) <= 0.00005) {
        this.velocity = 0;
        this.snapToNearest();
      }
    } else if (this.shouldAutoRotate()) {
      this.phi += this.speed * dt;
      moving = true;
    }

    this.render();

    this.rafId = moving || this.snapping ? requestAnimationFrame(this.tick) : null;
  }

  /* Snapping ------------------------------------------------------------ */

  snapToIndex(index) {
    const desired = FRONT - index * this.step;
    this.target = this.phi + shortestDelta(this.phi, desired);
    this.snapping = true;
    this.velocity = 0;
    this.start();
  }

  snapToNearest() {
    const raw = (FRONT - this.phi) / this.step;
    const index = ((Math.round(raw) % this.count) + this.count) % this.count;
    this.snapToIndex(index);
  }

  /* Interaction --------------------------------------------------------- */

  onPointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;

    this.dragging = true;
    this.snapping = false;
    this.velocity = 0;
    this.moved = 0;
    this.lastX = event.clientX;
    this.lastMoveTime = event.timeStamp;
    this.pointerId = event.pointerId;

    try {
      this.stage.setPointerCapture(event.pointerId);
    } catch (e) {
      /* capture is best-effort */
    }

    this.classList.add('is-dragging');
    this.start();
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.lastX;
    this.lastX = event.clientX;
    this.moved += Math.abs(dx);

    // Map horizontal travel to arc travel, so drag feel is radius-independent.
    const delta = (dx / Math.max(this.rx, 1)) * 1.15;
    this.phi += delta;

    const dt = Math.max(event.timeStamp - this.lastMoveTime, 1);
    this.lastMoveTime = event.timeStamp;
    // Smoothed rad/ms, used as the hand-off velocity for inertia.
    this.velocity = this.velocity * 0.6 + (delta / dt) * 0.4;
  }

  onPointerUp(event) {
    if (!this.dragging) return;
    this.dragging = false;
    this.classList.remove('is-dragging');

    try {
      this.stage.releasePointerCapture(event.pointerId);
    } catch (e) {
      /* capture is best-effort */
    }

    if (Math.abs(this.velocity) <= 0.00005 || this.reduced.matches) {
      this.velocity = 0;
      this.snapToNearest();
    } else {
      this.start();
    }
  }

  onClickCapture(event) {
    // A drag that ended over a link must not navigate.
    if (this.moved > DRAG_THRESHOLD) {
      event.preventDefault();
      event.stopPropagation();
      this.moved = 0;
      return;
    }

    const item = event.target.closest('[data-orbit-item]');
    if (!item) return;

    const index = this.items.indexOf(item);
    if (index === -1) return;

    // Clicking a product that is not active selects it; clicking the active
    // product follows its link.
    if (index !== this.activeIndex) {
      event.preventDefault();
      event.stopPropagation();
      this.snapToIndex(index);
    }
  }

  onWheel(event) {
    // Only claim clearly horizontal trackpad gestures. Vertical page scroll is
    // never intercepted.
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    event.preventDefault();
    this.snapping = false;
    this.phi -= (event.deltaX / Math.max(this.rx, 1)) * 0.9;

    clearTimeout(this.wheelTimer);
    this.wheelTimer = setTimeout(() => this.snapToNearest(), 140);
    this.start();
  }

  onKeyDown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const next = (this.activeIndex + direction + this.count) % this.count;
    this.snapToIndex(next);
    const link = this.items[next].querySelector('a');
    if (link) link.focus({ preventScroll: true });
  }

  onFocusIn(event) {
    const item = event.target.closest('[data-orbit-item]');
    if (!item) return;
    const index = this.items.indexOf(item);
    if (index !== -1 && index !== this.activeIndex) this.snapToIndex(index);
  }

  onEnter() {
    this.hovering = true;
  }

  onLeave() {
    this.hovering = false;
    if (this.shouldAutoRotate()) this.start();
  }

  onVisibility() {
    if (!document.hidden && this.shouldAutoRotate()) this.start();
  }

  onResize() {
    this.measure();
    this.render();
  }

  onMotionChange() {
    if (this.reduced.matches) {
      this.velocity = 0;
      this.snapToNearest();
    } else if (this.shouldAutoRotate()) {
      this.start();
    }
  }
}

function shortestDelta(from, to) {
  let delta = (to - from) % TWO_PI;
  if (delta > Math.PI) delta -= TWO_PI;
  if (delta < -Math.PI) delta += TWO_PI;
  return delta;
}

function preventDefault(event) {
  event.preventDefault();
}

if (!customElements.get('orbit-collection')) {
  customElements.define('orbit-collection', OrbitCollection);
}